import type { BrandAppNews, BrandAppOfferCard } from './brandAppContract'

const HINT_LINE_LENGTH = 36
const IMAGE_WIDTH = 400
const FRAME_INSET = 14
const MIN_PADDING = 22
const BUTTON_WIDTH = 184

interface EmptyLayout {
  icon: number
  iconRadius: number
  titleGap: number
  titleSize: number
  hintGap: number
  hintSize: number
  hintLine: number
  buttonGap: number
  buttonHeight: number
}

const REGULAR: EmptyLayout = {
  icon: 44,
  iconRadius: 12,
  titleGap: 14,
  titleSize: 18,
  hintGap: 8,
  hintSize: 15,
  hintLine: 21,
  buttonGap: 20,
  buttonHeight: 38
}

const COMPACT: EmptyLayout = {
  icon: 40,
  iconRadius: 11,
  titleGap: 10,
  titleSize: 17,
  hintGap: 6,
  hintSize: 14,
  hintLine: 19,
  buttonGap: 14,
  buttonHeight: 34
}

const IMAGE_HEIGHTS: Record<EmptyBlockKind, number> = { offers: 300, news: 300, menu: 225, hero: 320, social: 225 }
const HERO_TILE_BAND = 118

const svgDataUrl = (svg: string) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`

const escapeXml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export const wrapLines = (text: string, maxLength = HINT_LINE_LENGTH): string[] =>
  text
    .split(/\s+/)
    .filter(Boolean)
    .reduce<string[]>((lines, word) => {
      const last = lines[lines.length - 1]
      if (last !== undefined && `${last} ${word}`.length <= maxLength) lines[lines.length - 1] = `${last} ${word}`
      else lines.push(word)

      return lines
    }, [])

export type EmptyBlockKind = 'offers' | 'news' | 'menu' | 'hero' | 'social'

const ICONS: Record<EmptyBlockKind, string> = {
  offers:
    '<path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4z"/><path d="M13 7v10"/>',
  news: '<path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/>',
  menu: '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>',
  hero: '<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>',
  social:
    '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>'
}

export interface EmptyBlockImageTexts {
  kind: EmptyBlockKind
  title: string
  hint: string
  cta: string
}

const contentHeight = (layout: EmptyLayout, lineCount: number, hasIcon: boolean): number =>
  (hasIcon ? layout.icon + layout.titleGap : 0) +
  layout.titleSize +
  layout.hintGap +
  lineCount * layout.hintLine +
  layout.buttonGap +
  layout.buttonHeight

export const emptyBlockImage = ({ kind, title, hint, cta }: EmptyBlockImageTexts): string => {
  const height = IMAGE_HEIGHTS[kind]
  const lines = wrapLines(hint)
  const band = kind === 'hero' ? HERO_TILE_BAND : 0
  const hasIcon = kind !== 'hero'
  const fits = (layout: EmptyLayout) =>
    contentHeight(layout, lines.length, hasIcon) + band + 2 * (FRAME_INSET + MIN_PADDING) <= height
  const layout = fits(REGULAR) ? REGULAR : COMPACT
  const iconBlock = hasIcon ? layout.icon + layout.titleGap : 0
  const upperHeight = iconBlock + layout.titleSize + layout.hintGap + lines.length * layout.hintLine
  const bandTop = height / 2 - band / 2
  const innerTop = FRAME_INSET + MIN_PADDING
  const innerBottom = height - FRAME_INSET - MIN_PADDING
  const iconTop = band
    ? innerTop + (bandTop - innerTop - upperHeight) / 2
    : (height - contentHeight(layout, lines.length, hasIcon)) / 2
  const titleBase = iconTop + iconBlock + layout.titleSize - 3
  const hintBase = titleBase + layout.hintGap + layout.hintLine
  const buttonTop = band
    ? bandTop + band + (innerBottom - bandTop - band - layout.buttonHeight) / 2
    : iconTop + upperHeight + layout.buttonGap
  const buttonLeft = IMAGE_WIDTH / 2 - BUTTON_WIDTH / 2
  const iconScale = (layout.icon * 0.6) / 24
  const iconOffset = IMAGE_WIDTH / 2 - 12 * iconScale
  const hintText = lines
    .map(
      (line, index) =>
        `<text x="200" y="${hintBase + index * layout.hintLine}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="${layout.hintSize}" fill="#71717A">${escapeXml(line)}</text>`
    )
    .join('')

  return svgDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${IMAGE_WIDTH} ${height}" width="${IMAGE_WIDTH}" height="${height}">` +
      `<rect width="${IMAGE_WIDTH}" height="${height}" fill="#FFFFFF"/>` +
      `<rect x="${FRAME_INSET}" y="${FRAME_INSET}" width="${IMAGE_WIDTH - 2 * FRAME_INSET}" height="${height - 2 * FRAME_INSET}" rx="14" fill="none" stroke="#D4D4D8" stroke-width="2" stroke-dasharray="7 6"/>` +
      (hasIcon
        ? `<rect x="${IMAGE_WIDTH / 2 - layout.icon / 2}" y="${iconTop}" width="${layout.icon}" height="${layout.icon}" rx="${layout.iconRadius}" fill="#F4F4F5"/>` +
          `<g transform="translate(${iconOffset} ${iconTop + layout.icon / 2 - 12 * iconScale}) scale(${iconScale})" fill="none" stroke="#52525B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ICONS[kind]}</g>`
        : '') +
      `<text x="200" y="${titleBase}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="${layout.titleSize}" font-weight="600" fill="#09090B">${escapeXml(title)}</text>` +
      hintText +
      `<rect x="${buttonLeft}" y="${buttonTop}" width="${BUTTON_WIDTH}" height="${layout.buttonHeight}" rx="8" fill="#FFFFFF" stroke="#E4E4E7" stroke-width="1.5"/>` +
      `<text x="200" y="${buttonTop + layout.buttonHeight / 2 + 5}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="14" font-weight="500" fill="#09090B">${escapeXml(cta)}</text>` +
      '</svg>'
  )
}

export interface PlaceholderTexts {
  title: string
  description: string
  ctaLabel: string
}

export interface EmptyBlockTexts extends PlaceholderTexts {
  hint: string
  cta: string
}

export const emptyOffers = ({ hint, cta, ...texts }: EmptyBlockTexts): BrandAppOfferCard[] => [
  {
    offerId: -1,
    rewardId: -1,
    image: emptyBlockImage({ kind: 'offers', title: texts.title, hint, cta }),
    ...texts,
    isPlaceholder: true
  }
]

export const emptyNews = ({ hint, cta, ...texts }: EmptyBlockTexts): BrandAppNews[] => [
  {
    id: 'placeholder-1',
    image: emptyBlockImage({ kind: 'news', title: texts.title, hint, cta }),
    ...texts,
    ctaTarget: { type: 'screen', screen: 'loyalty' },
    isPlaceholder: true
  }
]
